/**
 * useSignupSheets / useSignupSheetDetail (ported from flock-expo/src/hooks/useSignupSheets.ts)
 *
 * Same query keys as the Expo app. Differences: no dashboard invalidation
 * (web has no home feed), archived sheets fetched via a lazy query instead
 * of imperative state, and every service call pins the owning org's ID.
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  signupSheetsService,
  type SheetUpdates,
  type SlotUpdates,
} from '@/services/signupSheets.service';
import { useAuth } from '@/context/AuthContext';
import type { SignupSheet, CreateSheetPayload, AddSlotPayload } from '@flock-types/signupSheets.types';

export interface UseSignupSheetsReturn {
  sheets: SignupSheet[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createSheet: (data: CreateSheetPayload) => Promise<SignupSheet>;
}

export function useSignupSheets(
  groupId: string | null,
  organizationId?: string
): UseSignupSheetsReturn {
  const { currentOrganization, isSwitchingOrg } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['signup-sheets', groupId];

  const sheetsQuery = useQuery({
    queryKey,
    queryFn: () => signupSheetsService.getSheets(groupId!, 'active', organizationId),
    // Gate on !isSwitchingOrg so refetches during the claims-verification
    // window don't fire with NEW_TOKEN + OLD_GROUP_ID and 403 against the
    // new org's RLS context.
    enabled: !!currentOrganization?.id && !!groupId && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateSheetPayload) =>
      signupSheetsService.createSheet(groupId!, data, organizationId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['signup-sheets', groupId] });
  }, [queryClient, groupId]);

  return {
    sheets: sheetsQuery.data ?? [],
    loading: sheetsQuery.isLoading,
    error: sheetsQuery.error ? 'Failed to load sign-up sheets' : null,
    refetch,
    createSheet: createMutation.mutateAsync,
  };
}

/** Archived sheets, fetched lazily when the leader expands the toggle. */
export function useArchivedSheets(
  groupId: string | null,
  enabled: boolean,
  organizationId?: string
) {
  const { currentOrganization, isSwitchingOrg } = useAuth();

  const query = useQuery({
    queryKey: ['signup-sheets-archived', groupId],
    queryFn: () => signupSheetsService.getSheets(groupId!, 'archived', organizationId),
    enabled: enabled && !!currentOrganization?.id && !!groupId && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return { sheets: query.data ?? [], loading: query.isLoading };
}

export interface UseSignupSheetDetailReturn {
  sheet: SignupSheet | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  signUp: (slotId: string, note?: string) => Promise<void>;
  vote: (slotId: string) => Promise<void>;
  withdraw: (slotId: string) => Promise<void>;
  addSlot: (payload: AddSlotPayload) => Promise<void>;
  addSlotsBulk: (slots: AddSlotPayload[]) => Promise<void>;
  updateSheet: (updates: SheetUpdates) => Promise<void>;
  deleteSheet: () => Promise<void>;
  deleteSlot: (slotId: string) => Promise<void>;
  deleteAllSlots: () => Promise<void>;
  updateEntry: (entryId: string, note: string | null) => Promise<void>;
  deleteEntry: (entryId: string) => Promise<void>;
  assignMember: (slotId: string, userId: string) => Promise<void>;
  updateSlot: (slotId: string, updates: SlotUpdates) => Promise<void>;
  isMutating: boolean;
}

export function useSignupSheetDetail(
  sheetId: string | null,
  organizationId?: string
): UseSignupSheetDetailReturn {
  const { currentOrganization, isSwitchingOrg } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['signup-sheet-detail', sheetId];

  const detailQuery = useQuery({
    queryKey,
    queryFn: () => signupSheetsService.getSheetDetail(sheetId!, organizationId),
    enabled: !!currentOrganization?.id && !!sheetId && !isSwitchingOrg,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['signup-sheet-detail', sheetId] });
    queryClient.invalidateQueries({ queryKey: ['signup-sheets'] });
    queryClient.invalidateQueries({ queryKey: ['signup-sheets-archived'] });
  }, [queryClient, sheetId]);

  const signUpMutation = useMutation({
    mutationFn: async ({ slotId, note }: { slotId: string; note?: string }) => {
      await signupSheetsService.signUp(slotId, note, organizationId);
    },
    onSettled: invalidate,
  });

  const voteMutation = useMutation({
    mutationFn: async (slotId: string) => {
      await signupSheetsService.vote(slotId, organizationId);
    },
    onSettled: invalidate,
  });

  const withdrawMutation = useMutation({
    mutationFn: async (slotId: string) => {
      await signupSheetsService.withdraw(slotId, organizationId);
    },
    onSettled: invalidate,
  });

  const addSlotMutation = useMutation({
    mutationFn: async (payload: AddSlotPayload) => {
      await signupSheetsService.addSlot(sheetId!, payload, organizationId);
    },
    onSettled: invalidate,
  });

  const addSlotsBulkMutation = useMutation({
    mutationFn: async (slots: AddSlotPayload[]) => {
      await signupSheetsService.addSlotsBulk(sheetId!, slots, organizationId);
    },
    onSettled: invalidate,
  });

  const updateSheetMutation = useMutation({
    mutationFn: async (updates: SheetUpdates) => {
      await signupSheetsService.updateSheet(sheetId!, updates, organizationId);
    },
    onSettled: invalidate,
  });

  const deleteSheetMutation = useMutation({
    mutationFn: async () => {
      await signupSheetsService.deleteSheet(sheetId!, organizationId);
    },
    onSettled: invalidate,
  });

  const deleteSlotMutation = useMutation({
    mutationFn: async (slotId: string) => {
      await signupSheetsService.deleteSlot(slotId, organizationId);
    },
    onSettled: invalidate,
  });

  const deleteAllSlotsMutation = useMutation({
    mutationFn: async () => {
      await signupSheetsService.deleteAllSlots(sheetId!, organizationId);
    },
    onSettled: invalidate,
  });

  const updateEntryMutation = useMutation({
    mutationFn: async ({ entryId, note }: { entryId: string; note: string | null }) => {
      await signupSheetsService.updateEntry(entryId, note, organizationId);
    },
    onSettled: invalidate,
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (entryId: string) => {
      await signupSheetsService.deleteEntry(entryId, organizationId);
    },
    onSettled: invalidate,
  });

  const assignMemberMutation = useMutation({
    mutationFn: async ({ slotId, userId }: { slotId: string; userId: string }) => {
      await signupSheetsService.assignMember(slotId, userId, organizationId);
    },
    onSettled: invalidate,
  });

  const updateSlotMutation = useMutation({
    mutationFn: async ({ slotId, updates }: { slotId: string; updates: SlotUpdates }) => {
      await signupSheetsService.updateSlot(slotId, updates, organizationId);
    },
    onSettled: invalidate,
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['signup-sheet-detail', sheetId] });
  }, [queryClient, sheetId]);

  return {
    sheet: detailQuery.data ?? null,
    loading: detailQuery.isLoading,
    error: detailQuery.error ? 'Failed to load sign-up sheet' : null,
    refetch,
    signUp: async (slotId: string, note?: string) => {
      await signUpMutation.mutateAsync({ slotId, note });
    },
    vote: voteMutation.mutateAsync,
    withdraw: withdrawMutation.mutateAsync,
    addSlot: addSlotMutation.mutateAsync,
    addSlotsBulk: addSlotsBulkMutation.mutateAsync,
    updateSheet: updateSheetMutation.mutateAsync,
    deleteSheet: deleteSheetMutation.mutateAsync,
    deleteSlot: deleteSlotMutation.mutateAsync,
    deleteAllSlots: deleteAllSlotsMutation.mutateAsync,
    updateEntry: async (entryId: string, note: string | null) => {
      await updateEntryMutation.mutateAsync({ entryId, note });
    },
    deleteEntry: deleteEntryMutation.mutateAsync,
    assignMember: async (slotId: string, userId: string) => {
      await assignMemberMutation.mutateAsync({ slotId, userId });
    },
    updateSlot: async (slotId: string, updates: SlotUpdates) => {
      await updateSlotMutation.mutateAsync({ slotId, updates });
    },
    isMutating:
      signUpMutation.isPending ||
      voteMutation.isPending ||
      withdrawMutation.isPending ||
      addSlotMutation.isPending ||
      addSlotsBulkMutation.isPending ||
      updateSheetMutation.isPending ||
      deleteSheetMutation.isPending ||
      deleteSlotMutation.isPending ||
      deleteAllSlotsMutation.isPending ||
      updateEntryMutation.isPending ||
      deleteEntryMutation.isPending ||
      assignMemberMutation.isPending ||
      updateSlotMutation.isPending,
  };
}
